import { Alert } from "reactstrap";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useTypedNavigate from "../hooks/typed-navigate";
import { PASSWORD_HINT } from "../utils/authErrors";
import { DEFAULT_AFTER_AUTH, nextFromSearch, withNext } from "../utils/authRedirect";
import { useSeo } from "../hooks/useSeo";
import type { SignupOutcome } from "../actions/user";
import ConfirmSignup from "./ConfirmSignup";
import GoogleSignInButton from "./GoogleSignInButton";
import TurnstileWidget, { TURNSTILE_SITE_KEY } from "./TurnstileWidget";

interface Props {
  auth: AuthenticationState;
  signupAction: (email: string, password: string, turnstileToken?: string) => Promise<SignupOutcome>;
  confirmSignupAction: (email: string, code: string, password: string) => Promise<SignupOutcome>;
  clearError: () => void;
}

export default function SignupPage(props: Props) {
  useSeo({ title: "Sign Up", path: "/signup", noindex: true });
  const { auth, signupAction, confirmSignupAction, clearError } = props;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const needsTurnstile = !!TURNSTILE_SITE_KEY && !turnstileToken;
  const navigate = useTypedNavigate();
  const location = useLocation();
  const destination = nextFromSearch(location.search) ?? DEFAULT_AFTER_AUTH;

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (auth.loggedIn) {
      navigate(destination, { replace: true });
    }
  }, [auth.loggedIn, navigate, destination]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // A previous attempt's error must not read as a verdict on what's typed now.
    if (auth.errorMessage) clearError();
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
    if (name === "confirmPassword") {
      setConfirmPassword(value);
      setPasswordMismatch(false);
    }
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setSubmitted(true);
      setPasswordMismatch(false);

      if (!email || !password || !confirmPassword) return;

      if (password !== confirmPassword) {
        setPasswordMismatch(true);
        return;
      }
      if (needsTurnstile) return;

      const outcome = await signupAction(email, password, turnstileToken ?? undefined);
      if (outcome === "confirmationRequired") {
        setAwaitingCode(true);
        return;
      }
      navigate(destination, { replace: true });
    } catch (error) {
      // Turnstile tokens are single-use; the failed attempt spent this one.
      setTurnstileReset((n) => n + 1);
      console.error(error);
    }
  };

  const errorAlert = (
    <Alert color="danger" isOpen={!!auth.errorMessage}>
      {auth.errorMessage}
      {auth.errorAction === "login" && (
        <> <Link to={withNext("/login", location.search)}>Log in instead</Link>.</>
      )}
      {auth.errorAction === "reset" && (
        <> <Link to={withNext("/forgot-password", location.search)}>Reset your password</Link>.</>
      )}
    </Alert>
  );

  if (awaitingCode) {
    return (
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-auto login-container">
            <ConfirmSignup
              email={email}
              busy={auth.signingUp}
              onConfirm={async (code) => {
                await confirmSignupAction(email, code, password);
                navigate(destination, { replace: true });
              }}
            />
            {errorAlert}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-md-center">
        <div className="col-md-auto login-container">
          <h2>Sign Up</h2>
          <GoogleSignInButton next={destination} />
          <form name="form" onSubmit={handleSubmit}>
            <div
              className={
                "form-group" + (submitted && !email ? " has-error" : "")
              }
            >
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={email}
                onChange={handleChange}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
              {submitted && !email && (
                <div className="help-block" style={{ color: "red" }}>
                  Email is required
                </div>
              )}
            </div>
            <div
              className={
                "form-group" + (submitted && !password ? " has-error" : "")
              }
            >
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                value={password}
                onChange={handleChange}
                autoComplete="new-password"
                minLength={8}
                aria-describedby="password-hint"
                required
              />
              <small id="password-hint" className="form-text text-muted">
                {PASSWORD_HINT}
              </small>
              {submitted && !password && (
                <div className="help-block" style={{ color: "red" }}>
                  Password is required
                </div>
              )}
            </div>
            <div
              className={
                "form-group" +
                (submitted && !confirmPassword ? " has-error" : "")
              }
            >
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                minLength={8}
                required
              />
              {submitted && !confirmPassword && (
                <div className="help-block" style={{ color: "red" }}>
                  Please confirm your password
                </div>
              )}
              {passwordMismatch && (
                <div className="help-block" style={{ color: "red" }}>
                  Passwords do not match
                </div>
              )}
            </div>
            <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} />
            <div className="form-group">
              <button className="btn btn-primary" disabled={auth.signingUp || needsTurnstile}>
                {auth.signingUp ? <><span className="spinner-border spinner-border-sm" role="status" /> Signing up...</> : "Sign Up"}
              </button>
            </div>
          </form>
          {errorAlert}
          <p className="mt-3">
            Already have an account? <Link to={withNext("/login", location.search)}>Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
